import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPhoneNumbersV1Service, serviceName } from '../service';
import { ListUserAccessiblePhoneNumbersIO, ServiceQueries } from '../types';
import { usePhoneNumbersV1Queries } from './use-phone-numbers-v1-queries';
import { UsePhoneNumbersV1QueryEndpointArgs, usePhoneNumbersV1Query } from './use-phone-numbers-v1-query';

/**
 * A hook that returns a query for the `ListUserAccessiblePhoneNumbers` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListUserAccessiblePhoneNumbersQuery = <E = unknown, D = ListUserAccessiblePhoneNumbersIO['output']>(
  args: UsePhoneNumbersV1QueryEndpointArgs<'ListUserAccessiblePhoneNumbers', E, D>
) =>
  usePhoneNumbersV1Query({
    endpointName: 'ListUserAccessiblePhoneNumbers',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListUserAccessiblePhoneNumbers` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListUserAccessiblePhoneNumbersQueries = <E = unknown, D = ListUserAccessiblePhoneNumbersIO['output']>(
  argsArr: UsePhoneNumbersV1QueryEndpointArgs<'ListUserAccessiblePhoneNumbers', E, D>[]
) =>
  usePhoneNumbersV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListUserAccessiblePhoneNumbers',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListUserAccessiblePhoneNumbers` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListUserAccessiblePhoneNumbers = () => {
  const queryClient = useQueryClient();

  const fetchListUserAccessiblePhoneNumbers = async <E = unknown, D = ListUserAccessiblePhoneNumbersIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UsePhoneNumbersV1QueryEndpointArgs<'ListUserAccessiblePhoneNumbers', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListUserAccessiblePhoneNumbersIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListUserAccessiblePhoneNumbers'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListUserAccessiblePhoneNumbersIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListUserAccessiblePhoneNumbers'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListUserAccessiblePhoneNumbers'>({
          serviceName,
          endpointName: 'ListUserAccessiblePhoneNumbers',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaPhoneNumbersV1Service.ListUserAccessiblePhoneNumbers(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListUserAccessiblePhoneNumbers;
};
